import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  Home, MessageSquare, Users, Calendar, BarChart3,
  Settings, HelpCircle, LogOut, Menu, Shield, ArrowLeft, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

/* BC-022: grouped sidebar + top header. Same five destinations as the
   "simpler by design" spec; Settings and Help sit in their own group. */
const MAIN_NAV = [
  { href: "/home", label: "Home", icon: Home, alt: ["/my-chatbots", "/chatbot/"] },
  { href: "/conversations", label: "Conversations", icon: MessageSquare, alt: ["/workspace/chat-history"] },
  { href: "/contacts", label: "Contacts", icon: Users, alt: ["/workspace/leads"] },
  { href: "/appointments", label: "Appointments", icon: Calendar, alt: ["/workspace/appointments"] },
  { href: "/insights", label: "Insights", icon: BarChart3, alt: ["/workspace/visitor-analytics"] },
];
const ACCOUNT_NAV = [
  { href: "/settings", label: "Settings", icon: Settings, alt: ["/settings", "/account", "/workspace/", "/admin/"] },
  { href: "/contact-us", label: "Help", icon: HelpCircle },
];

const isActive = (path, { href, alt = [] }) =>
  path === href || alt.some((a) => path.startsWith(a));

function sectionTitle(path) {
  if (path.startsWith("/chatbot/")) {
    if (path.endsWith("/training")) return "Assistant / Training";
    if (path.endsWith("/settings")) return "Assistant / Settings";
    return "Assistant";
  }
  if (path.startsWith("/activity")) return "Recent activity";
  const all = [...MAIN_NAV, ...ACCOUNT_NAV];
  return (all.find((n) => isActive(path, n)) || { label: "Bicarra" }).label;
}

function NavItem({ item, path, onClose }) {
  const { href, label, icon: Icon } = item;
  const active = isActive(path, item);
  return (
    <Link
      to={href}
      onClick={onClose}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group flex items-center gap-3 h-10 px-3 rounded-[8px] text-[14px] transition-colors ${
        active
          ? "bg-b2-wash text-b2-indigo font-semibold"
          : "text-b2-inksoft font-medium hover:text-b2-indigo hover:bg-b2-canvas"
      }`}
    >
      <Icon
        className={`w-[18px] h-[18px] shrink-0 ${active ? "text-b2-indigo" : "text-b2-muted group-hover:text-b2-indigo"}`}
        strokeWidth={active ? 2.1 : 1.8}
      />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Sidebar({ onClose }) {
  const path = useLocation().pathname;
  return (
    <div className="h-full flex flex-col bg-b2-surface border-r border-b2-line">
      <Link
        to="/home" onClick={onClose}
        className="flex items-center gap-2.5 h-16 px-6 shrink-0 border-b border-b2-line"
      >
        <img src="/brand/bicarra-icon.svg" alt="" className="w-7 h-7 rounded-[7px]" />
        <span className="text-[20px] font-bold tracking-[-0.03em] text-b2-ink lowercase">bicarra</span>
      </Link>

      <nav className="flex-1 overflow-y-auto px-4 pt-6 pb-4">
        <p className="b2-label px-3 mb-2">Menu</p>
        <div className="space-y-0.5">
          {MAIN_NAV.map((item) => <NavItem key={item.href} item={item} path={path} onClose={onClose} />)}
        </div>
        <p className="b2-label px-3 mt-7 mb-2">Account</p>
        <div className="space-y-0.5">
          {ACCOUNT_NAV.map((item) => <NavItem key={item.href} item={item} path={path} onClose={onClose} />)}
        </div>
      </nav>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || user?.email || "there").split(/[\s@]/)[0];
  const initials = (user?.name || user?.email || "?")
    .split(/[\s@]/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-2.5 h-10 pl-1 pr-2 rounded-full hover:bg-b2-canvas transition-colors"
        >
          <span className="w-8 h-8 rounded-full bg-b2-indigo text-white flex items-center justify-center text-[12px] font-semibold shrink-0">
            {initials}
          </span>
          <span className="hidden sm:flex flex-col items-start leading-tight text-left">
            <span className="text-[11px] font-medium text-b2-live">
              {user?.is_platform_admin ? "Administrator" : "Signed in"}
            </span>
            <span className="text-[13px] font-semibold text-b2-ink max-w-[140px] truncate">{firstName}</span>
          </span>
          <ChevronDown className="hidden sm:block w-4 h-4 text-b2-faint" strokeWidth={1.8} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-3 py-2.5 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-b2-wash text-b2-indigo flex items-center justify-center text-[12px] font-semibold shrink-0">
            {initials}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-b2-muted truncate">{user?.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/contact-us" className="cursor-pointer">
            <HelpCircle className="w-4 h-4 mr-2" /> Help
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          data-testid="logout-menu-item"
          className="text-red-600 focus:text-red-600 cursor-pointer"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardLayout({ children }) {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const isImpersonating = !!sessionStorage.getItem("___admin_token");

  const handleBackToAdmin = async () => {
    const adminToken = sessionStorage.getItem("___admin_token");
    if (!adminToken) return;
    sessionStorage.removeItem("___admin_token");
    try { sessionStorage.setItem("___at", btoa(adminToken)); } catch {}
    await checkAuth();
    toast.success("Switched back to admin");
    navigate("/admin/users");
  };

  return (
    <div className="b2 b2-app min-h-screen">
      <aside className="hidden lg:block fixed top-0 left-0 h-screen w-[240px] z-30">
        <Sidebar />
      </aside>

      <div className="lg:ml-[240px] min-h-screen flex flex-col">
        {isImpersonating && (
          <div className="bg-b2-attentionwash text-b2-attention text-sm px-4 py-2 flex items-center justify-between border-b border-b2-line">
            <span className="flex items-center gap-2 font-medium">
              <Shield className="w-4 h-4" /> Managing as {user?.name} ({user?.email})
            </span>
            <Button
              size="sm" variant="outline" data-testid="back-to-admin-btn"
              className="h-7 bg-white border-b2-linestrong text-b2-ink"
              onClick={handleBackToAdmin}
            >
              <ArrowLeft className="w-3 h-3 mr-1" /> Back to admin
            </Button>
          </div>
        )}

        <header className="sticky top-0 z-20 h-16 bg-b2-surface border-b border-b2-line flex items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden -ml-2" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5 text-b2-inksoft" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 border-0">
              <div className="b2 b2-app h-full"><Sidebar onClose={() => setMobileOpen(false)} /></div>
            </SheetContent>
          </Sheet>
          <Link to="/home" className="lg:hidden flex items-center gap-2">
            <img src="/brand/bicarra-icon.svg" alt="" className="w-6 h-6 rounded-[6px]" />
            <span className="text-[17px] font-bold tracking-[-0.03em] lowercase text-b2-ink">bicarra</span>
          </Link>
          <p className="hidden lg:block text-[13px] text-b2-muted">
            <span className="text-b2-faint">Bicarra</span>
            <span className="mx-2 text-b2-faint">/</span>
            <span className="font-medium text-b2-inksoft">{sectionTitle(path)}</span>
          </p>
          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/contact-us" title="Help"
              className="hidden sm:flex w-10 h-10 items-center justify-center rounded-full text-b2-muted hover:text-b2-indigo hover:bg-b2-canvas"
            >
              <HelpCircle className="w-[19px] h-[19px]" strokeWidth={1.8} />
            </Link>
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8 dashboard-page">
          <div className="mx-auto max-w-[1200px]">{children}</div>
        </main>

        <footer className="px-4 sm:px-6 lg:px-8 py-5 border-t border-b2-line bg-b2-surface text-[12.5px] text-b2-muted flex flex-wrap gap-x-5 gap-y-1 justify-between">
          <span>&copy; {new Date().getFullYear()} Bicarra</span>
          <span className="flex gap-4">
            <Link to="/privacy" className="hover:text-b2-indigo">Privacy</Link>
            <Link to="/terms" className="hover:text-b2-indigo">Terms</Link>
            <Link to="/contact-us" className="hover:text-b2-indigo">Help</Link>
          </span>
        </footer>
      </div>
    </div>
  );
}
